import React, { useEffect, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Typography } from '@pkg/components';
import YatNFTCollection from '@pkg/components/Sections/Profile/NFTCollection';
import YatTextReveal from '@pkg/components/Sections/Profile/TextReveal';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { getNftDetailsUrl } from '@app/helpers/url';
import { setEditingItemId } from '@app/store/actions/yat-page-item-actions';
import { previewYatPageItem } from '@app/store/slices';
import { type ContentItem, type NFTContent } from '@app/store/types/yat-page-actions';

import YatPreview from '../../Preview';

import {
    ContentWrapper,
    GridItemNFTWrapper,
    InformationWrapper,
    NFT_OUTLINE_OFFSET,
    StyledNFTPreviewButton,
    StyledYatAnimatedOutline,
    StyledYatNFT,
    StyledYatRemoveButton,
} from './styles';

interface Props {
    item: ContentItem<NFTContent>;
    editMode?: boolean;
}

export const YatGridItemNFT = ({ item, editMode }: Props) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { usingMouse } = useMediaQuery();
    const draggingId = useAppSelector((state) => state.yatPage.draggingId);
    const draggingItems = useAppSelector((state) => state.nftManager.draggingItems);
    const dragging = draggingId === item.id;
    const previewing = draggingItems.some((currentItem) => currentItem === item.id);
    const [wasPreviewed, setWasPreviewed] = useState(false);
    const [delayedPreviewing, setDelayedPreviewing] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const hoveringRef = useRef<boolean>(false);
    const draggingRef = useRef<boolean>(false);
    const inPage = useAppSelector((state) => state.yatPage.pageAssets.sorted);
    const addedToYatPage = inPage.some((currentItem) => currentItem === item.id);

    // Store previewing state to show different load in if previewing vs on load
    useEffect(() => {
        if (previewing) setWasPreviewed(true);
    }, [previewing, theme]);

    useEffect(() => {
        if (dragging) {
            // `dragging` is always false when `onClick` fires, so store reference for when click is released
            draggingRef.current = true;

            // If dragging, ignore any preview previewing state
            setWasPreviewed(false);
        }
    }, [dragging]);

    useEffect(() => {
        if (editMode || !wrapperRef.current) return;

        wrapperRef.current.style.cursor = hoveringRef.current && !dragging ? 'pointer' : '';
    }, [dragging, editMode]);

    // To prevent NFT outline and InformationWrapper showing too soon after !previewing, delay when those show
    useEffect(() => {
        if (previewing) setDelayedPreviewing(true);
        else {
            const timeout = setTimeout(() => setDelayedPreviewing(false), theme.transitions.speeds.normal * 1000);
            return () => clearTimeout(timeout);
        }
    }, [previewing, theme]);

    const handleClick = () => {
        // If editing or dragging, ignore route
        if (!editMode && !draggingRef.current) {
            const contractAddress = item.content?.contract_address;
            const tokenId = item.content?.token_id;
            if (contractAddress && tokenId) {
                const detailsUrl = getNftDetailsUrl({
                    contractAddress,
                    tokenId,
                    goBackEnabled: true,
                });
                navigate(detailsUrl);
            }
        }

        if (!usingMouse && editMode) {
            dispatch(setEditingItemId(item.id));
            setTimeout(() => {
                wrapperRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }, 400);
        }

        // Reset dragging ref on click
        draggingRef.current = false;
    };

    const handleMouseMove = () => (hoveringRef.current = true);

    const handleMouseLeave = () => (hoveringRef.current = false);

    return (
        <GridItemNFTWrapper ref={wrapperRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <StyledNFTPreviewButton
                canShow={editMode && !dragging && !previewing}
                onClick={() => dispatch(previewYatPageItem(item, navigate, addedToYatPage))}
            />
            <StyledYatRemoveButton canShow={editMode && !dragging && !previewing} item={item} />

            <ContentWrapper $outlineOffset={NFT_OUTLINE_OFFSET} $isEditing={editMode} onClick={handleClick}>
                <StyledYatAnimatedOutline
                    show={!delayedPreviewing && !dragging}
                    outlineOffset={NFT_OUTLINE_OFFSET}
                    interactWithCursorOnHover={!previewing}
                    primaryColor={theme.colors.pink}
                    secondaryColor={theme.colors.purple}
                />

                <StyledYatNFT
                    image={item?.content?.image}
                    video={item?.content?.video}
                    fallback={item?.content?.fallbackAssetUrl}
                    dragging={dragging}
                    $wasPreviewed={wasPreviewed}
                    $previewing={previewing}
                />

                <YatPreview show={previewing} delayShow />
            </ContentWrapper>

            <InformationWrapper $outlineOffset={NFT_OUTLINE_OFFSET}>
                {item?.content?.title && (
                    <Typography type="headline" size={24}>
                        <AnimatePresence>
                            <YatTextReveal
                                text={item.content.title}
                                state={delayedPreviewing ? 'initial' : !dragging ? 'animate' : 'exit'}
                                preventOverflow
                            />
                        </AnimatePresence>
                    </Typography>
                )}
                <YatNFTCollection
                    verified={item?.content?.verified}
                    collection={item?.content?.collection_name}
                    collectionSlug={item?.content?.slug}
                    hidden={delayedPreviewing || dragging}
                />
            </InformationWrapper>
        </GridItemNFTWrapper>
    );
};
